import * as React from 'react';
import { useState, useMemo } from 'react';
import { css } from '@emotion/core';
import { COLORS } from '../../../globalStyle/colors';
import LocalDiv from '../../../localization/LocalDiv';
import { FLEX_COLUMN, FLEX_ROW } from '../../../layout/flexBox';
import LocalBtn from '../../../localization/LocalBtn';
import { Str } from '../../../localization/translationKeys';
import { btnStyle, BTN_SHIMMER } from '../../../forms/styles/button';
import Spacer from '../../../layout/Spacer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import AddRaterForm from './AddRaterForm';
import { RaterLevel, Rater } from './models';
import { TextDirection } from '../../../localization/textDirection';
import RaterList from './RaterList';
import { useAppState } from '../../../core/redux/hooks';
import { Language } from '../../../localization/models';
import { HIDE_ON_MOBILE } from '../../../globalStyle/mediaQueries';
import { isValidDate } from '../../../language/time';
import Msg from '../../../localization/Msg';
import ClamShell from '../../../layout/ClamShell';
import { isNilOrEmpty } from '../../../language/module';
import { Requirement } from '../../models';
import { Link } from 'react-router-dom';
import { Routes } from '../../../navigation/routes';


const styles = {
  root:             css`
    & > * { padding-top: 8px; }
  `,
  msgBar:           {
    root: css`
      background-color: ${COLORS.WHITE};
      padding: 8px;
      border: 0 none;
      min-height: 50px;
    `,
  },
  addFormContainer: css`
    background-color: ${COLORS.LIGHTEST_GREY};
    border-top: 1px solid ${COLORS.LIGHTER_GREY};
    padding: 8px;
    justify-content: space-between;
  `,
  button:           css`
    min-width: 132px;
    justify-content: center;
    align-items: center;
  `,
  inviteButtons:    css` //Buttons, forms, etc.
    & > * {
      margin: 4px;
    }
  `,
  errorMsg:         {
    root:    css`
      align-items: center;
      margin: auto 8px;
    `,
    icon:    css`
      color: ${COLORS.RED};
      height: min-content;
      padding: 0 8px;
    `,
    msgText: css`
      color: ${COLORS.RED};
      background-color: transparent;
    `,
  },
  infoMsg:          {
    msgText: css`
      color: ${COLORS.GREEN};
      background-color: transparent;
    `,
  },
};


type Props = {
  className?: string;

  routes: Routes;

  code: string;

  // Rater list data
  raters: Rater[];
  raterLevels: RaterLevel[];
  projectLanguages: Language[];
  requirements: Requirement[];
  undeliveredEmails: string[];

  // Edit rater data state
  raterUnderEdit: Rater | undefined;
  markRaterForEdit: (rater: Rater | undefined) => void;
  cancelRaterEdit: () => void;
  desyncedRaterId?: number;
  updatingRater: boolean;

  // Messaging
  addingRater?: boolean;
  errorAddingRater?: boolean;

  errorAddingPreviousRater: boolean;

  sendingInvitations: boolean;
  invitationsSent: boolean;

  sendingForApproval: boolean;
  approverSent: boolean;

  // Approval feature
  approval: boolean;
  approverIsAdminSelected: boolean;
  adminSelectedApproverName?: string;
  approvalLevelId: number;
  approverEmailSent: boolean;

  // Rater names
  showNameFields: boolean;
  requireNamesOnAdd: boolean;

  // Rater Roster Parameters
  minimumsRequired: boolean;
  maximumsRequired: boolean;

  // Feature visibility
  enableAddPreviousRaters: boolean;
  enableDirectorySearch: boolean;
  enableManualEntry: boolean;

  // Button disables
  surveyPastDue: boolean;
  disableCustomizeInviteButton: boolean;
  disableCustomizeEmailButton: boolean;
  disableAddPreviousRatersButton: boolean;

  // UI state actions
  setEmail: (email: string) => void;
  toggleCustomizeInviteModal: () => void;
  toggleCustomizeApproverEmailModal: () => void;
  toggleMobileRequirements: () => void;

  // API actions
  addPreviousRaters: () => void;
  addRater: (levelId: number, email: string, firstName: string, lastName: string) => void;
  updateRater: (
    raterId: number,
    email?: string,
    undelivered?: boolean,
    firstName?: string,
    lastName?: string,
    raterLevel?: RaterLevel,
    language?: Language,
  ) => void;
  deleteRater: (raterId: number) => void;
  sendInvitations: () => void;
  setApprover: (raterId: number) => void;
  sendForApproval: () => void;
};


/**
 * RaterTable: a component that renders a list of raters and their invitation status. Also presents the tools necessary
 * to add raters to the roster and send out invites.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
const RaterTable: React.FC<Props> = props => {
  const [addRaterErrorMsg, setAddRaterErrorMsg]   = useState<string | undefined>(undefined);
  const [editRaterErrorMsg, setEditRaterErrorMsg] = useState<string | undefined>(undefined);

  const dir        = useAppState(state => state.locale.textDirection);
  const tableAlign = dir === TextDirection.LTR ? 'left' : 'right';

  const validationErrorMsg = addRaterErrorMsg || editRaterErrorMsg;
  const hasValidationError = !!validationErrorMsg;

  const uninvitedRaters = useMemo(() => props.raters.filter(rater => !isValidDate(rater.invitedDate)), [props.raters]);
  const bouncedRaters   = useMemo(() => props.raters.filter(rater => rater.undeliveredEmail), [props.raters]);
  const approvalReady   = useMemo(
    () => props.raters.filter(rater => rater.approvalStatus === 0 || rater.approvalStatus === 2),
    [props.raters],
  );

  const approvalLevel     = useMemo(
    () => props.raterLevels.find(level => level.surveyLevelId === props.approvalLevelId),
    [props.raterLevels, props.approvalLevelId],
  );
  const approvalLevelName = approvalLevel?.fullName ?? '';

  const levelCount    = useMemo(
    () => props.raters.filter(rater => rater.levelId === props.approvalLevelId).length,
    [props.raters, props.approvalLevelId],
  );
  const approverCount = useMemo(() => props.raters.filter(rater => rater.approvalStatus === 1).length, [props.raters]);
  const notApproved = useMemo(
    () => props.raters.filter(rater => rater.approvalStatus === 0 || rater.approvalStatus === 2).length,
    [props.raters],
  );

  const projReqs = props.requirements;

  const needMoreRaters = useMemo(
    () => props.minimumsRequired && projReqs.filter(req => req.raterCount < req.minimumRequired).length > 0,
    [projReqs, props.minimumsRequired],
  );

  const showReqInvite   = !props.approval && needMoreRaters;
  const showInvite      = !props.approval && !isNilOrEmpty(uninvitedRaters);
  const showLevel       = props.approval && props.approvalLevelId > 0 && levelCount === 0;
  const showSelApprover = props.approval && props.approvalLevelId > 0 && approverCount === 0;
  const showReqApproval = props.approval && needMoreRaters;
  const showApproval    = props.approval && notApproved > 0;

  const showReqApprovalAdminSelected = props.approverIsAdminSelected && notApproved > 0;

  const requiredInviteAlertMsg = (showReqInvite) && (
    <Msg str={Str.RequiredRatersInviteMsg} css={styles.infoMsg.msgText} />
  );

  const uninvitedAlertMsg = (showInvite) && (
    <Msg str={Str.UninvitedRatersMsg} replace={{ 'number': uninvitedRaters.length }} css={styles.infoMsg.msgText} />
  );

  const levelAlertMsg = (showLevel) && (
    <Msg str={Str.InviteApprovalMsg} replace={{ 'levelName': approvalLevelName }} css={styles.infoMsg.msgText} />
  );

  const selAppoverMsg = (showSelApprover) && (
    <Msg str={Str.ChooseApprover} css={styles.infoMsg.msgText} />
  );

  const requiredApprovalAlertMsg = (showReqApproval) && (
    <Msg str={Str.RequiredRatersApprovalMsg} css={styles.infoMsg.msgText} />
  );

  const adminSelectedApprovalAlertMsg = (showReqApprovalAdminSelected) && (
    <Msg
      str={Str.InviteApprovalMsg3}
      replace={{ 'firstLast': props.adminSelectedApproverName }}
      css={styles.infoMsg.msgText}
    />
  );

  const approvalAlertMsg = (showApproval) && (
    <Msg str={Str.InviteApprovalMsg2} css={styles.infoMsg.msgText} />
  );

  const showInviteSuccess   = props.invitationsSent;
  const showApproverSuccess = props.approverSent;

  const showError = hasValidationError
                    || props.errorAddingRater
                    || props.errorAddingPreviousRater
                    || !!props.desyncedRaterId;

  const showPrompt = showReqInvite
                     || showInvite
                     || showLevel
                     || showSelApprover
                     || showReqApproval
                     || showReqApprovalAdminSelected
                     || showApproval;

  const showBounced = !isNilOrEmpty(bouncedRaters);

  const msgIconColor = showError ? COLORS.RED : COLORS.GREEN;

  const errorMsgTxt = hasValidationError
    ? ''
    : props.errorAddingPreviousRater || !!props.desyncedRaterId
      ? Str.UnableToProcess
      : Str.AddFailed;

  /* first prompt message appears in the following priority order */
  const promptAlertMsg = (showPrompt) && (
    showReqInvite
      ? requiredInviteAlertMsg
      : showInvite
        ? uninvitedAlertMsg
        : showLevel
          ? levelAlertMsg
          : showSelApprover
            ? selAppoverMsg
            : showReqApproval
              ? requiredApprovalAlertMsg
              : showReqApprovalAdminSelected
                ? adminSelectedApprovalAlertMsg
                : approvalAlertMsg
  );

  /* undelivered message always shows up - 2nd item if prompt is shown */
  const bouncedAlertMsg = (showBounced) && (
    <Msg str={Str.UndeliveredRaters} replace={{ 'number': bouncedRaters.length }} css={styles.errorMsg.msgText} />
  );

  const project = useAppState(state => state.projectSummary);
  const pastDue = useMemo(() => project?.surveyPastDue, [project]);

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]}>

      {/* Top bar (error/info message, invite actions) */}
      {
          !pastDue && (
            <div css={[FLEX_ROW, styles.msgBar.root]}>
              <ClamShell
                  show={showInviteSuccess || showApproverSuccess || showError || showPrompt || showBounced}
                  css={[FLEX_ROW, styles.errorMsg.root]}
                >
                <FontAwesomeIcon icon={faExclamationTriangle} css={css`color: ${msgIconColor};`} size={'2x'} />
                <Spacer sizePx={10} />
                <LocalDiv css={FLEX_COLUMN}>
                  {
                    showInviteSuccess
                      ? <Msg str={Str.InvitesSent} css={styles.infoMsg.msgText} />
                      : showApproverSuccess
                      ? <Msg str={Str.ApprovalRequestSent} css={styles.infoMsg.msgText} />
                      : showError
                        ? <Msg str={errorMsgTxt} defaultMsg={validationErrorMsg} css={styles.errorMsg.msgText} />
                        : (
                          <>
                            {promptAlertMsg}
                            {bouncedAlertMsg}
                          </>
                        )
                  }
                </LocalDiv>
              </ClamShell>
    
              <Spacer css={css`margin: auto;`} />
    
              <div css={[FLEX_ROW, styles.inviteButtons]}>
                <LocalBtn
                  str={props.approval ? Str.CustomizeEmail : Str.CustomizeInvite}
                  css={[styles.button, btnStyle.transparent(COLORS.GREEN), HIDE_ON_MOBILE]}
                  onClick={props.approval ? props.toggleCustomizeApproverEmailModal : props.toggleCustomizeInviteModal}
                  disabled={props.approval ? props.disableCustomizeEmailButton : props.disableCustomizeInviteButton}
                />
                <div css={[FLEX_ROW, css`position: relative;`]}>
                  {
                    (props.approval)
                      ? (
                        <LocalBtn
                          css={[
                            FLEX_ROW,
                            btnStyle.primary(COLORS.GREEN),
                            styles.button,
                            props.sendingForApproval && BTN_SHIMMER,
                            HIDE_ON_MOBILE,
                          ]}
                          disabled={!approvalReady.length || needMoreRaters || props.surveyPastDue}
                          str={Str.SendForApproval}
                          icon={faEnvelope}
                          onClick={props.sendForApproval}
                        />
                      )
                      : (
                        <LocalBtn
                          css={[
                            FLEX_ROW,
                            btnStyle.primary(COLORS.GREEN),
                            styles.button,
                            props.sendingInvitations && BTN_SHIMMER,
                            HIDE_ON_MOBILE,
                          ]}
                          disabled={
                            !uninvitedRaters.length
                            || needMoreRaters
                            || props.surveyPastDue
                            || props.sendingInvitations
                            || props.updatingRater
                            || !!props.desyncedRaterId
                          }
                          str={Str.InviteRaters}
                          icon={faEnvelope}
                          onClick={props.sendInvitations}
                        />
                      )
                  }
                </div>
              </div>
          </div>
        )
      }
      {/* Add rater actions (new and previous) */}
      {
          !pastDue && (
            <div css={[FLEX_ROW, styles.addFormContainer]}>
              { props.enableManualEntry && (
                  <AddRaterForm
                    raterLevels={props.raterLevels}
                    requirements={props.requirements || []}
                    addRater={props.addRater}
                    addingRater={props.addingRater}
                    setEmailAddress={props.setEmail}
                    setErrorMsg={setAddRaterErrorMsg}
                    showNameFields={props.showNameFields}
                    requireNamesOnAdd={props.requireNamesOnAdd}
                    maximumsRequired={props.maximumsRequired}
                    approver={false}
                  />
                )
              }
              <LocalDiv css={FLEX_COLUMN}>
                {
                  props.enableDirectorySearch && !pastDue && (
                    <Link
                      to={props.routes.SearchDirectory}
                      css={[
                        btnStyle.primary(COLORS.DARK_GREY),
                        styles.button,
                        HIDE_ON_MOBILE,
                        css`text-decoration: none; margin: 4px;`,
                      ]}
                    >
                      <Msg str={Str.SearchDirectory} />
                    </Link>
                  )
                }
                {
                  props.enableAddPreviousRaters && !pastDue && (
                    <LocalBtn
                      css={[
                        FLEX_ROW,
                        btnStyle.transparent(COLORS.DARK_GREY),
                        styles.button,
                        HIDE_ON_MOBILE,
                        css`margin: 4px;`,
                      ]}
                      str={Str.AddPreviousRaters}
                      onClick={props.addPreviousRaters}
                      disabled={props.disableAddPreviousRatersButton}
                    />
                  )
                }
              </LocalDiv>
            </div>
          )
      }
      <RaterList
        raters={props.raters}
        raterLevels={props.raterLevels}
        projectLanguages={props.projectLanguages}
        undeliveredEmails={props.undeliveredEmails}
        raterUnderEdit={props.raterUnderEdit}
        markRaterForEdit={props.markRaterForEdit}
        cancelRaterEdit={props.cancelRaterEdit}
        desyncedRaterId={props.desyncedRaterId}
        showNameFields={props.showNameFields}
        approval={props.approval}
        hideApproverColumn={props.approverIsAdminSelected}
        approvalLevelId={props.approvalLevelId}
        approvalLevelCount={levelCount}
        approverEmailSent={props.approverEmailSent || props.approverSent}
        setErrorMsg={setEditRaterErrorMsg}
        updateRater={props.updateRater}
        deleteRater={props.deleteRater}
        tableAlign={tableAlign}
        setApprover={props.setApprover}
        code={props.code}
        requirements={props.requirements || []}
        maximumsRequired={props.maximumsRequired}
      />
    </LocalDiv>
  );
};

export default RaterTable;
